/**
 * +atmos: shared atmosphere uniforms (time of day, sun, cloud shadows) for every outdoor material.
 *
 * FOR OTHER MATERIALS (props, POIs, critters, new shaders): every material made through style.ts
 * (`toonMaterial`, `toonColor`, `ownedToonMaterial`, `stylize`) already gets the cloud shadows and
 * the time-of-day key / fill colours for free; nothing to do. A custom ShaderMaterial that wants
 * them imports this module and:
 *   - adds `ATMOS.uSunDir`, `ATMOS.uCloudShadow`, `ATMOS.uCloudTex` to its `uniforms` (share the
 *     objects, don't copy them: they are updated in place every frame);
 *   - pastes `CLOUD_SHADOW_GLSL` into the fragment shader and multiplies its sunlit term by
 *     `cloudShadow(worldPos)` (1 = clear sky, down to 1 - strength under a cloud);
 *   - `ATMOS.uDay` (0 night … 1 day) and `ATMOS.uSunColor` / `ATMOS.uSkyColor` are there for
 *     emissive tricks (lanterns brighter at night, sky-tinted reflections).
 * Presentation only: nothing here feeds the sim.
 */
import * as THREE from 'three';

/** Size (yd) of one tile of the cloud-shadow pattern on the ground. */
export const CLOUD_TILE_YD = 300;

function makeCloudShadowTexture(): THREE.DataTexture {
  // Tileable soft blobs: periodic value-noise fbm, thresholded softly (big shapes, soft edges).
  const N = 128;
  const P = 8; // lattice period (cells across the tile)
  const lat = new Float32Array(P * P * 4);
  let s = 1234567;
  const rnd = (): number => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
  for (let i = 0; i < lat.length; i++) lat[i] = rnd();
  const vn = (x: number, y: number, per: number, o: number): number => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const fx = x - xi;
    const fy = y - yi;
    const u = fx * fx * (3 - 2 * fx);
    const v = fy * fy * (3 - 2 * fy);
    const at = (a: number, b: number): number => lat[o + (((b % per) + per) % per) * per + (((a % per) + per) % per)]!;
    const a = at(xi, yi);
    const b = at(xi + 1, yi);
    const c = at(xi, yi + 1);
    const d = at(xi + 1, yi + 1);
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
  };
  const data = new Uint8Array(N * N);
  for (let j = 0; j < N; j++)
    for (let i = 0; i < N; i++) {
      const x = (i / N) * 4;
      const y = (j / N) * 4;
      const n = vn(x, y, 4, 0) * 0.62 + vn(x * 2, y * 2, 8, 16) * 0.28 + vn(x * 4, y * 4, 8, 80) * 0.1;
      const t = Math.min(1, Math.max(0, (n - 0.5) / 0.16));
      data[j * N + i] = Math.round(t * t * (3 - 2 * t) * 255);
    }
  const tex = new THREE.DataTexture(data, N, N, THREE.RedFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  tex.name = 'atmos-cloud-shadow';
  return tex;
}

export const ATMOS = {
  /** Unit vector toward the sun (three.js world space); the key light sits along it. */
  uSunDir: { value: new THREE.Vector3(-0.55, 1.0, 0.62).normalize() },
  /** Sun / moon light colour (linear, includes intensity ~1 at noon). */
  uSunColor: { value: new THREE.Color(1, 0.94, 0.82) },
  /** Sky (fill) colour at the horizon. */
  uSkyColor: { value: new THREE.Color(0.58, 0.81, 0.92) },
  /** 1 = full day … 0 = night. */
  uDay: { value: 1 },
  /** Cloud shadows: x = 1 / tile size (yd), y / z = drift offset (yd), w = strength (0 = off: dungeons). */
  uCloudShadow: { value: new THREE.Vector4(1 / CLOUD_TILE_YD, 0, 0, 0) },
  uCloudTex: { value: makeCloudShadowTexture() },
};

/** `float cloudShadow(vec3 worldPos)`: 1 in sunlight, 1 - strength under a drifting cloud. */
export const CLOUD_SHADOW_GLSL = `
uniform vec4 uCloudShadow; uniform sampler2D uCloudTex; uniform vec3 uSunDir;
float cloudShadow(vec3 wp){
  if (uCloudShadow.w <= 0.0) return 1.0;
  // Project along the sun onto a cloud layer ~120 yd up (shadows slide with a low sun).
  vec2 q = wp.xz + uSunDir.xz / max(uSunDir.y, 0.25) * (120.0 - wp.y);
  float c = texture2D(uCloudTex, (q + uCloudShadow.yz) * uCloudShadow.x).r;
  return 1.0 - c * uCloudShadow.w;
}`;
